import type { ReactNode } from 'react';
import {
    CalendarIcon,
    CartIcon,
    CheckThickIcon,
    EllipseFilledIcon,
    EllipseHalfIcon,
    LadderIcon,
    LocationIcon,
    OpenIcon,
    WorldIcon,
} from '@bi-digital/icons';
import { Button, Card, CardContent, CardHeader, CardMedia, Chip, useMediaQuery } from '@bi-digital/react';
import { SoftHyphen } from '@bino/utils/components';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useCartContext } from '../../../context/CartContext';
import type { ApplyItem, Arrangement, CustomApplyItem, Programme, Term } from '../../../types';
import styles from './StudyCard.module.css';

export type StudyCardProps = {
    item: ApplyItem;
    programme: Programme;
    itemBeingAdded: string | null;
    addToCart: (arrangement: Arrangement) => void;
};

export const applyItemKey = (item: ApplyItem) =>
    item.type === 'arrangement' ? item.bannerId : `${item.url}-${item.campus}-${item.load}`;

export const StudyCard = ({ item, programme, itemBeingAdded, addToCart }: StudyCardProps) => {
    return item.type === 'arrangement' ? (
        <ArrangementCard
            arrangement={item}
            programme={programme}
            itemBeingAdded={itemBeingAdded}
            addToCart={addToCart}
        />
    ) : (
        <CustomApplyCard item={item} programme={programme} />
    );
};

const campusImage = (campus: Arrangement['campus'], semester: Term['semester']) => {
    switch (campus) {
        case 'Oslo':
            return semester === 'Fall'
                ? '/public/images/campuses/campus-oslo-fall.jpg'
                : '/public/images/campuses/campus-oslo-spring.jpg';
        case 'Bergen':
            return '/public/images/campuses/campus-bergen.jpg';
        case 'Stavanger':
            return '/public/images/campuses/campus-stavanger.jpg';
        case 'Trondheim':
            return '/public/images/campuses/campus-trondheim.jpg';
        case 'Online':
        default:
            return '/public/images/campuses/campus-web.jpg';
    }
};

type CardShellProps = {
    programme: Programme;
    campus: Arrangement['campus'];
    term: Term;
    load: Arrangement['load'];
    chips: ReactNode;
    className?: string;
    children: ReactNode;
};

const CardShell = ({ programme, campus, term, load, chips, className, children }: CardShellProps) => {
    const { t } = useTranslation();
    const isLargeScreen = useMediaQuery('(min-width: 1128px)');
    const chipSize = isLargeScreen ? 'medium' : 'small';

    const imageAltText = campus !== 'Online' ? `${t('cart:campusAltText')} ${t(`cart:campus.${campus}`)}` : '';

    return (
        <Card orientation='vertical' className={classNames(styles.card, className)}>
            <CardMedia
                overlay={
                    <Chip
                        data-color={campus === 'Online' ? 'info' : 'neutral'}
                        size={chipSize}
                        startIcon={<LocationIcon title='Location' />}
                    >
                        {t(`cart:campus.${campus}`)}
                    </Chip>
                }
            >
                <img alt={imageAltText} src={campusImage(campus, term.semester)} />
            </CardMedia>
            <CardContent>
                <CardHeader
                    header={
                        <span className={styles.cardTitle}>
                            <SoftHyphen text={programme.name} />
                        </span>
                    }
                    label={programme.label}
                    subLabel={
                        programme.credits ? (
                            <span className={styles.cardHeaderSubLabel}>
                                {t('cart:credits', { count: programme.credits })}
                            </span>
                        ) : undefined
                    }
                    className={styles.cardHeader}
                />

                <div className={styles.chipsContainer}>
                    <Chip
                        data-color='neutral'
                        size={chipSize}
                        startIcon={
                            load === 'FullTime' ? (
                                <EllipseFilledIcon aria-hidden='true' />
                            ) : (
                                <EllipseHalfIcon aria-hidden='true' />
                            )
                        }
                    >
                        {t(`cart:load.${load}`)}
                    </Chip>
                    <Chip data-color='neutral' size={chipSize} startIcon={<CalendarIcon aria-hidden='true' />}>
                        {t(`cart:${term.semester.toLowerCase()}`, { year: term.year })}
                    </Chip>
                    <Chip data-color='neutral' size={chipSize} startIcon={<WorldIcon aria-hidden='true' />}>
                        {t(`cart:language.${programme.teachingLanguage}`)}
                    </Chip>
                    {chips}
                </div>

                {children}
            </CardContent>
        </Card>
    );
};

export type ArrangementCardProps = Omit<StudyCardProps, 'item'> & {
    arrangement: Arrangement;
};

export const ArrangementCard = ({ arrangement, programme, itemBeingAdded, addToCart }: ArrangementCardProps) => {
    const { t } = useTranslation();
    const { cart } = useCartContext();
    const isLargeScreen = useMediaQuery('(min-width: 1128px)');
    const chipSize = isLargeScreen ? 'medium' : 'small';

    return (
        <CardShell
            programme={programme}
            campus={arrangement.campus}
            term={arrangement.term}
            load={arrangement.load}
            className={!arrangement.isOpenForApplicants ? styles.notOpenForApplicants : undefined}
            chips={
                (arrangement.studentType === 'SecondYear' || arrangement.studentType === 'ThirdYear') && (
                    <Chip size={chipSize} startIcon={<LadderIcon aria-hidden='true' />} data-color='warning'>
                        {t('cart:studentypeText') + ' ' + t(`cart:studentype.${arrangement.studentType}`)}
                    </Chip>
                )
            }
        >
            {!arrangement.isOpenForApplicants ? (
                <Button fullwidth variant='text' disabled className={styles.addButton}>
                    {t('cart:notOpenForApplicants')}
                </Button>
            ) : cart.items.some((item) => item.bannerId === arrangement.bannerId) ? (
                <div className={styles.addedToCartText}>
                    <CheckThickIcon size='medium' aria-hidden='true' />
                    <p>{t('cart:addedToApplication')}</p>
                </div>
            ) : (
                <Button
                    fullwidth
                    name='addtocart'
                    variant='flat'
                    startIcon={<CartIcon aria-hidden='true' />}
                    onClick={() => addToCart(arrangement)}
                    submitting={itemBeingAdded === arrangement.bannerId}
                    disabled={itemBeingAdded === arrangement.bannerId}
                    className={styles.addButton}
                >
                    {t('cart:addtocart')}
                </Button>
            )}
        </CardShell>
    );
};

type CustomApplyCardProps = {
    item: CustomApplyItem;
    programme: Programme;
};

const CustomApplyCard = ({ item, programme }: CustomApplyCardProps) => {
    const { t } = useTranslation();

    return (
        <CardShell programme={programme} campus={item.campus} term={item.term} load={item.load} chips={null}>
            <Button
                asChild
                fullwidth
                variant='flat'
                startIcon={<OpenIcon aria-hidden='true' />}
                className={styles.addButton}
            >
                <a href={item.url} target='_blank' rel='noopener noreferrer'>
                    {item.applyText ?? t('cart:applyExternal')}
                </a>
            </Button>
        </CardShell>
    );
};
